/* eslint-disable no-shadow */
import router from '@/router';
import isEqual from 'lodash.isequal';
import omit from 'lodash.omit';

const initialState = {
  marketplaceParams: {
    s: [],
    l: [],
    f: [],
    p: '',
    o: '',
  },
  defaults: { // defaults to setup ui if there are no params
    defaultState: 'all',
    defaultSort: 'end_time',
  },
  persistedAfterLogout: {
    sort: '',
  },
};

export const state = {
  ...initialState,
};

export const mutations = {
  setMarketplaceParams(state, marketplaceParams = {}) {
    state.marketplaceParams = marketplaceParams;
  },
  retainDefaults(state, { defaultState = 'all', defaultSort = 'end_time' }) {
    state.defaults = {
      ...state.defaults,
      defaultState,
      defaultSort,
    };
  },
  setPersistedAfterLogout(state, persistedAfterLogout = {}) {
    state.persistedAfterLogout = {
      ...state.persistedAfterLogout,
      ...persistedAfterLogout,
    };
  },
  handleRouterPush(state, validQueryToUse = {}) {
    // handles the router push on url changes from user actions
    router.push({
      query: validQueryToUse,
    });
  },
};

/*
* Each action for a marketplace param should already be validated to be applicable
*/
export const actions = {
  // when saved searches change, handle the router push for changes
  updateMarketplaceSavedSearchIds(
    { commit, rootState, state },
    { savedSearchIds = [] },
  ) {
    if (savedSearchIds.length) {
      // when saved searches change, remove quick search filters
      if (!isEqual(state.marketplaceParams.s, savedSearchIds)) {
        commit('setMarketplaceParams', {
          ...state.marketplaceParams,
          s: savedSearchIds,
          f: [],
        });

        commit('handleRouterPush', {
          ...omit(rootState.route.query, 'f'),
          s: JSON.stringify(savedSearchIds),
        });
      }
    } else if (rootState.route.query.s) {
      // if there's no more saved searches, remove from query
      commit('setMarketplaceParams', {
        ...state.marketplaceParams,
        s: [],
      });

      commit('handleRouterPush', {
        ...omit(rootState.route.query, 's'),
      });
    }
  },

  // when lanes change, handle the router push for changes
  updateMarketplaceLanes({ commit, rootState, state }, { lanes = [] }) {
    if (lanes.length) {
      // when lanes change, include them
      if (!isEqual(state.marketplaceParams.l, lanes)) {
        commit('setMarketplaceParams', {
          ...state.marketplaceParams,
          l: lanes,
        });

        commit('handleRouterPush', {
          ...rootState.route.query,
          l: JSON.stringify(lanes),
        });
      }
    } else if (rootState.route.query.l) {
      // if there's no more lanes, remove from query
      commit('setMarketplaceParams', {
        ...state.marketplaceParams,
        l: [],
      });

      commit('handleRouterPush', {
        ...omit(rootState.route.query, 'l'),
      });
    }
  },

  // when quick search filters change, handle the router push for changes
  updateMarketplaceQuickSearchFilters(
    { commit, rootState, state },
    { filters = [] },
  ) {
    if (filters.length) {
      // when filters change, remove saved searches
      if (!isEqual(state.marketplaceParams.f, filters)) {
        commit('setMarketplaceParams', {
          ...state.marketplaceParams,
          f: filters,
          s: [],
        });

        const filtersInUrl = filters.reduce((arr, f) => {
          let filter = f;

          if (filter.filterType === 'image') {
            // change the image value to the name display to not bulk browser url
            filter = {
              filterType: filter.filterType,
              value: filter.display.split('--')[0],

            };
          }
          arr.push(filter);
          return arr;
        }, []);

        commit('handleRouterPush', {
          ...omit(rootState.route.query, 's'),
          f: JSON.stringify(filtersInUrl),
        });
      }
    } else if (rootState.route.query.f) {
      // if there's no more filters, remove from query
      commit('setMarketplaceParams', {
        ...state.marketplaceParams,
        f: [],
      });

      commit('handleRouterPush', {
        ...omit(rootState.route.query, 'f'),
      });
    }
  },

  // when state filter changes, change the sort order if necessary
  // handle the router push for changes
  updateMarketplaceStateFilter(
    { commit, rootState, state },
    { stateFilter = '', sortOptions = {} },
  ) {
    if (stateFilter && stateFilter in sortOptions) {
      const foundSortOrder = sortOptions[stateFilter].find(
        (o) => o.value === state.marketplaceParams.o,
      );

      const sortOrderToUse = foundSortOrder
        ? state.marketplaceParams.o
        : sortOptions[stateFilter]?.[0]?.value;

      commit('setMarketplaceParams', {
        ...state.marketplaceParams,
        p: stateFilter,
        o: sortOrderToUse || state.defaults.defaultSort,
      });

      commit('handleRouterPush', {
        ...rootState.route.query,
        p: stateFilter,
        o: sortOrderToUse || state.defaults.defaultSort,
      });
    }
  },

  // when sort order changes, handle the router push for changes
  updateMarketplaceSortOrder({ commit, rootState, state }, { sortOrder = '' }) {
    if (sortOrder) {
      commit('setMarketplaceParams', {
        ...state.marketplaceParams,
        p: state.marketplaceParams.p || state.defaults.defaultState,
        o: sortOrder,
      });

      commit('handleRouterPush', {
        ...rootState.route.query,
        p: state.marketplaceParams.p || state.defaults.defaultState,
        o: sortOrder,
      });
    }
  },

  // general update of marketplace params, handle router push conditionally
  updateMarketplaceParams(
    { commit, rootState, state },
    { marketplaceParams = {}, routerPush = false },
  ) {
    commit('setMarketplaceParams', {
      ...state.marketplaceParams,
      ...marketplaceParams,
    });

    if (routerPush) {
      const validQuery = Object.keys(marketplaceParams).reduce((obj, key) => {
        const validObj = obj;

        if (['s', 'l', 'f'].includes(key) && marketplaceParams?.[key]?.length) {
          validObj[key] = JSON.stringify(marketplaceParams[key]);
        } else if (['p', 'o'].includes(key) && marketplaceParams?.[key]) {
          validObj[key] = marketplaceParams[key];
        }

        return obj;
      }, {});

      // handle bulk router push
      commit('handleRouterPush', {
        ...omit(rootState.route.query, ['s', 'l', 'f', 'p', 'o']),
        ...validQuery,
      });
    }
  },

  // reset the marketplace params to initial values
  resetMarketplaceParams({ commit, state }, { persistOnLogout = false } = {}) {
    if (persistOnLogout) {
      commit('setPersistedAfterLogout', {
        sort: state.marketplaceParams.o,
      });
    }

    commit('setMarketplaceParams', {
      ...initialState.marketplaceParams,
      p: state.defaults.defaultState,
      o: state.persistedAfterLogout.sort || state.defaults.defaultSort,
    });
  },

  persistDefaults({ commit }, { defaultState = 'all', defaultSort = 'end_time' }) {
    commit('retainDefaults', { defaultState, defaultSort });
  },
};

export const getters = {
  marketplaceSavedSearchIds: ({ marketplaceParams }) => marketplaceParams.s,
  marketplaceLanes: ({ marketplaceParams }) => marketplaceParams.l,
  marketplaceQuickSearchFilters: ({ marketplaceParams }) => marketplaceParams.f,
  marketplaceStateFilter: ({ marketplaceParams }) => marketplaceParams.p,
  marketplaceSortOrder: ({ marketplaceParams }) => marketplaceParams.o,
  marketplaceParams: ({ marketplaceParams }) => marketplaceParams,
};

const marketplaceRouter = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

export default marketplaceRouter;
